import { debounce } from 'lodash'

import { useInvoicing } from '.'
import { toast } from '@utils/functions/notificationToast'
import { parseAddEditOtherServicePayload } from '@pages/InvoicingClientModal/helpers/parseAddEditOtherServicePayload'
import { useClientModal } from '@pages/InvoicingClientModal/context'
import { parseEditDetailedSummaryPayload } from '../helpers/parseEditDetailedSummaryPayload'
import { initialPagination } from '@components/shared/Table/components/Pagination/types'

import type {
  ApprovalWorkflowRequestProps,
  BulkDistributeBillingRequestProps,
  GetClientsRequestProps,
  GetClWeeklyBreakdownRequestProps,
  GetDetailedSummaryRequestProps,
  GetPreviewDetailsRequestProps,
  PreviousInvoicingRequestProps
} from '@services/api/invoicing/types'
import type {
  GetOtherServicesRequestProps,
  RemoveOtherServiceRequestProps
} from '@services/api/invoicing/clients/types'
import type { AddEditOtherServicesModalFormProps } from '../components/AddEditOtherServicesModal/form/types'
import type { EditDetailedSummaryFormProps } from '../components/EditDetailedSummaryModal/form/types'
import type { AxiosError } from 'axios'

import * as apiInvoicing from '@services/api/invoicing'

const reversePreviewInvoicing = async (payload: PreviousInvoicingRequestProps) => {
  const { setIsReversePreviewInvoicingLoading } = useInvoicing.getState()

  setIsReversePreviewInvoicingLoading(true)

  try {
    await apiInvoicing.reversePreviewInvoicing(payload)

    toast.success('Prévia de faturamento revertida com sucesso!')

    getPreviewDetails({
      filiCode: payload.filiCode,
      competenceDate: payload.competenceDate
    })

    return true
  } catch (error) {
    toast.error('Não foi possível reverter a prévia de faturamento. Tente novamente', (error as AxiosError)?.status)

    return false
  } finally {
    setIsReversePreviewInvoicingLoading(false)
  }
}

const restorePreviewInvoicing = async (payload: PreviousInvoicingRequestProps) => {
  const {
    setIsRestorePreviewInvoicingLoading,
    clientsData,
    otherServicesData,
    weeklybreakdownData
  } = useInvoicing.getState()

  setIsRestorePreviewInvoicingLoading(true)

  try {
    await apiInvoicing.restorePreviewInvoicing(payload)

    toast.success('Prévia de faturamento restaurada com sucesso!')

    const { filiCode, competenceDate } = payload

    getPreviewDetails({
      filiCode,
      competenceDate
    })

    getDetailedSummary({
      filiCode,
      competenceDate
    })

    getClients({
      filiCodigo: filiCode,
      fatDtCompetencia: competenceDate,
      currentPage: clientsData?.currentPage || initialPagination.PAGE,
      itemsPerPage: clientsData?.itemsPerPage || initialPagination.ITEMS_PER_PAGE
    })

    getOtherServices({
      filiCode,
      competenceDate,
      currentPage: otherServicesData?.currentPage || initialPagination.PAGE,
      itemsPerPage: otherServicesData?.itemsPerPage || initialPagination.ITEMS_PER_PAGE
    })

    getWeeklybreakdown({
      filiCode,
      competenceDate,
      currentPage: weeklybreakdownData?.currentPage || initialPagination.PAGE,
      itemsPerPage: weeklybreakdownData?.itemsPerPage || initialPagination.ITEMS_PER_PAGE
    })

    return true
  } catch (error) {
    toast.error('Não foi possível restaurar a prévia de faturamento. Tente novamente', (error as AxiosError)?.status)

    return false
  } finally {
    setIsRestorePreviewInvoicingLoading(false)
  }
}

const releasePreviewInvoicing = async (payload: PreviousInvoicingRequestProps) => {
  const { setIsReleasePreviewInvoicingLoading } = useInvoicing.getState()

  setIsReleasePreviewInvoicingLoading(true)

  try {
    await apiInvoicing.releasePreviewInvoicing(payload)

    toast.success('Prévia de faturamento liberada com sucesso!')

    getPreviewDetails({
      filiCode: payload.filiCode,
      competenceDate: payload.competenceDate
    })

    return true
  } catch (error) {
    toast.error('Não foi possível liberar a prévia de faturamento. Tente novamente', (error as AxiosError)?.status)

    return false
  } finally {
    setIsReleasePreviewInvoicingLoading(false)
  }
}

const approvalWorkflow = async (payload: ApprovalWorkflowRequestProps) => {
  const { setIsApprovalWorkflowLoading } = useInvoicing.getState()

  setIsApprovalWorkflowLoading(true)

  try {
    await apiInvoicing.approvalWorkflow(payload)

    toast.success('Prévia enviada com sucesso!')

    getPreviewDetails({
      filiCode: payload.filiCode,
      competenceDate: payload.competenceDate
    })

    return true
  } catch (error) {
    const errorMessage =
      (error as AxiosError<{ errorMessage: string[] }>)?.response?.data?.errorMessage[0] || 'Falha ao enviar a prévia!'

    toast.error(errorMessage, (error as AxiosError)?.status)

    return false
  } finally {
    setIsApprovalWorkflowLoading(false)
  }
}

const getPreviewDetails = async (payload: GetPreviewDetailsRequestProps) => {
  const { setPreviewDetailsData, setIsPreviewDetailsLoading } = useInvoicing.getState()

  setIsPreviewDetailsLoading(true)

  try {
    const data = await apiInvoicing.getPreviewDetails(payload)

    setPreviewDetailsData(data)
  } catch (error) {
    toast.error('Falha ao buscar os detalhes da previa!', (error as AxiosError)?.status)
  } finally {
    setIsPreviewDetailsLoading(false)
  }
}

const editDetailedSummary = async (formData: EditDetailedSummaryFormProps) => {
  const { setIsEditDetailedSummaryLoading } = useInvoicing.getState()

  setIsEditDetailedSummaryLoading(true)

  try {
    const payload = parseEditDetailedSummaryPayload(formData)

    await apiInvoicing.editDetailedSummary(payload)

    toast.success('Resumo Detalhado atualizado com sucesso!')

    getDetailedSummary({
      filiCode: formData.filiCode,
      competenceDate: formData.competenceDate
    })

    getPreviewDetails({
      filiCode: formData.filiCode,
      competenceDate: formData.competenceDate
    })

    return true
  } catch (error) {
    toast.error('Não foi possível editar o Resumo detalhado. Tente novamente', (error as AxiosError)?.status)

    return false
  } finally {
    setIsEditDetailedSummaryLoading(false)
  }
}

const getDetailedSummary = async (payload: GetDetailedSummaryRequestProps) => {
  const { setDetailedSummaryData, setIsDetailedSummaryLoading } = useInvoicing.getState()

  setIsDetailedSummaryLoading(true)

  try {
    const data = await apiInvoicing.getDetailedSummary(payload)

    setDetailedSummaryData(data)
  } catch (error) {
    toast.error('Falha ao buscar o detalhamento semanal!', (error as AxiosError)?.status)
  } finally {
    setIsDetailedSummaryLoading(false)
  }
}

const getWeeklybreakdown = async (payload: GetClWeeklyBreakdownRequestProps) => {
  const { setWeeklybreakdownData, setIsWeeklybreakdownLoading } = useInvoicing.getState()

  setIsWeeklybreakdownLoading(true)

  try {
    const data = await apiInvoicing.getWeeklyBreakdown(payload)

    setWeeklybreakdownData(data)
  } catch (error) {
    toast.error('Falha ao buscar o detalhamento semanal!', (error as AxiosError)?.status)
  } finally {
    setIsWeeklybreakdownLoading(false)
  }
}

const removeOtherService = async (payload: RemoveOtherServiceRequestProps) => {
  const { setIsRemoveOtherServiceLoading } = useInvoicing.getState()

  setIsRemoveOtherServiceLoading(true)

  try {
    await apiInvoicing.removeOtherService(payload)

    toast.success('O outro serviço foi excluído com sucesso!')

    getOtherServices({
      competenceDate: payload.fatDtCompetencia,
      filiCode: payload.filiCode,
      currentPage: initialPagination.PAGE,
      itemsPerPage: initialPagination.ITEMS_PER_PAGE
    })

    getPreviewDetails({
      filiCode: payload.filiCode,
      competenceDate: payload.fatDtCompetencia
    })

    getDetailedSummary({
      filiCode: payload.filiCode,
      competenceDate: payload.fatDtCompetencia
    })

    return true
  } catch (error) {
    const errorMessage =
      (error as AxiosError<{ errorMessage: string[] }>)?.response?.data?.errorMessage[0] ||
      'Falha ao excluir o outro serviço!'

    toast.error(errorMessage, (error as AxiosError)?.status)

    return false
  } finally {
    setIsRemoveOtherServiceLoading(false)
  }
}

const editOtherService = async (formData: AddEditOtherServicesModalFormProps) => {
  const { setIsCreateNewOtherServiceLoading } = useClientModal.getState()

  setIsCreateNewOtherServiceLoading(true)

  try {
    const payload = parseAddEditOtherServicePayload(formData, true)

    await apiInvoicing.editOtherService(payload)

    toast.success('Novo serviço editado com sucesso!')

    getOtherServices({
      filiCode: formData.filiCode,
      competenceDate: formData.competenceDate,
      currentPage: initialPagination.PAGE,
      itemsPerPage: initialPagination.ITEMS_PER_PAGE
    })

    getPreviewDetails({
      filiCode: formData.filiCode,
      competenceDate: formData.competenceDate
    })

    getDetailedSummary({
      filiCode: formData.filiCode,
      competenceDate: formData.competenceDate
    })

    return true
  } catch (error) {
    toast.error('Não foi possível editar um novo serviço. Tente novamente', (error as AxiosError)?.status)

    return false
  } finally {
    setIsCreateNewOtherServiceLoading(false)
  }
}

const createNewOtherService = async (formData: AddEditOtherServicesModalFormProps) => {
  const { setIsCreateNewOtherServiceLoading } = useInvoicing.getState()

  setIsCreateNewOtherServiceLoading(true)

  try {
    const payload = parseAddEditOtherServicePayload(formData, true)

    await apiInvoicing.createNewOtherService(payload)

    toast.success('Novo serviço adicionado com sucesso!')

    getOtherServices({
      filiCode: formData.filiCode,
      competenceDate: formData.competenceDate,
      currentPage: initialPagination.PAGE,
      itemsPerPage: initialPagination.ITEMS_PER_PAGE
    })

    getPreviewDetails({
      filiCode: formData.filiCode,
      competenceDate: formData.competenceDate
    })

    getDetailedSummary({
      filiCode: formData.filiCode,
      competenceDate: formData.competenceDate
    })

    return true
  } catch (error) {
    toast.error('Não foi possível adicionar um novo serviço. Tente novamente', (error as AxiosError)?.status)

    return false
  } finally {
    setIsCreateNewOtherServiceLoading(false)
  }
}

const getOtherServices = async (payload: GetOtherServicesRequestProps) => {
  const { setOtherServicesData, setIsOtherServicesLoading } = useInvoicing.getState()

  setIsOtherServicesLoading(true)

  try {
    const data = await apiInvoicing.getOtherServices(payload)

    setOtherServicesData(data)
  } catch (error) {
    toast.error('Falha ao buscar os outros serviços!', (error as AxiosError)?.status)
  } finally {
    setIsOtherServicesLoading(false)
  }
}

const getClientsByFilter = debounce(async (payload: GetClientsRequestProps, searchText?: string) => {
  const { setClientsData, setIsClientsByFilterLoading } = useInvoicing.getState()

  if (searchText && searchText.length < 2) return

  setIsClientsByFilterLoading(true)

  try {
    const data = await apiInvoicing.getClients(payload, searchText)

    setClientsData(data)
  } catch (error) {
    toast.error('Falha ao filtrar os clientes!', (error as AxiosError)?.status)
  } finally {
    setIsClientsByFilterLoading(false)
  }
}, 800)

const bulkDistributeBilling = async (payload: BulkDistributeBillingRequestProps) => {
  const { setIsBulkDistributeBillingLoading } = useInvoicing.getState()

  setIsBulkDistributeBillingLoading(true)

  try {
    await apiInvoicing.bulkDistributeBilling(payload)

    toast.success('Faturamento distribuído com sucesso!')

    return true
  } catch (error) {
    toast.error('Não foi possível distribuir o faturamento. Tente novamente', (error as AxiosError)?.status)

    return false
  } finally {
    setIsBulkDistributeBillingLoading(false)
  }
}

const getClients = async (payload: GetClientsRequestProps) => {
  const { setClientsData, setIsClientsLoading } = useInvoicing.getState()

  setIsClientsLoading(true)

  try {
    const data = await apiInvoicing.getClients(payload)

    setClientsData(data)
  } catch (error) {
    toast.error('Falha ao buscar os clientes!', (error as AxiosError)?.status)
  } finally {
    setIsClientsLoading(false)
  }
}

export {
  bulkDistributeBilling,
  getClients,
  getClientsByFilter,
  getOtherServices,
  createNewOtherService,
  editOtherService,
  removeOtherService,
  getWeeklybreakdown,
  getDetailedSummary,
  editDetailedSummary,
  getPreviewDetails,
  approvalWorkflow,
  reversePreviewInvoicing,
  releasePreviewInvoicing,
  restorePreviewInvoicing
}
